"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/core/components/auth/store/auth";
import { useActionsTrack } from "@/core/services/menu-analytics";
import {
  clearScrolledToStart,
  hasChatState,
  hasScrolledToStart,
  markScrolledToStart,
  setConversationAgentType,
} from "@/core/utils/chatbot-storage";
import {
  GREETING,
  getClientSuggestionHandler,
  resolveDisplayTextForAction,
} from "../utils/chatbotUtils";
import {
  getAgentTypeForAction,
  REPORT_SCHEDULING_ACTION,
} from "../constants/agents";
import type { ParseEmailsResult } from "../utils/parseEmailsFromSpreadsheet";
import type { AgentShortcut, AgentType, Suggestion } from "../types";
import { useSendMessageStream } from "./useSendMessageStream";
import { useSendBlockAction } from "./useSendBlockAction";
import { useLiaChatHistoryDbMock } from "./useLiaChatHistoryDbMock";
import { useChatbot } from "../context/chatbot-context";

export type ChatSession = ReturnType<typeof useChatSession>;

/**
 * Motor compartilhado do chat dos agentes (PED-12262, Fase 2).
 * Concentra estado, streaming, histórico e handlers — é hospedado tanto
 * pela janela flutuante (FloatingChatbot) quanto pelo Centro de ações com IA
 * (página em tela cheia) e, na Fase 3, pelo painel lateral.
 *
 * `isActive` indica que a superfície do chat está visível (janela aberta ou
 * página montada): controla auto-scroll e o envio de mensagens pendentes.
 * `startFresh` monta a sessão sempre como conversa nova (welcome), sem
 * restaurar a última conversa — usado pelo Centro de ações com IA.
 */
export function useChatSession({
  isActive,
  startFresh = false,
}: {
  isActive: boolean;
  startFresh?: boolean;
}) {
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [extractionResult, setExtractionResult] = useState<
    (ParseEmailsResult & { fileName?: string; fileSize?: number }) | null
  >(null);
  const [extractionConfirmed, setExtractionConfirmed] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  const { onActionClick } = useActionsTrack();
  const { userInfo } = useAuth();
  const [translate] = useTranslation(["core"]);

  const companyId = userInfo?.user?.leagues_id?.[0];
  const appUserId = userInfo?.user?.id;

  const {
    pendingMessage,
    clearPendingMessage,
    registerChatMessagesUpdater,
    setForcedAgent,
    setActiveAgent,
    activeAgent,
  } = useChatbot();

  const {
    conversations,
    setConversations,
    activeConversationId,
    messages,
    setMessages,
    hydrated,
    actions,
  } = useLiaChatHistoryDbMock({
    companyId,
    userId: appUserId,
    greeting: GREETING,
    startFresh,
  });

  const endRef = useRef<HTMLDivElement>(null);
  const startRef = useRef<HTMLDivElement>(null);
  const shouldAutoScrollRef = useRef(true);

  // Rola apenas o viewport interno do chat. scrollIntoView rolaria também os
  // ancestrais overflow-hidden do shell (SidebarInset/wrapper docked),
  // deslocando a página inteira ao abrir o painel lateral (PED-12262).
  const scrollChatTo = useCallback(
    (
      ref: React.RefObject<HTMLDivElement>,
      position: "start" | "end",
      behavior: ScrollBehavior,
    ) => {
      const viewport = ref.current?.closest<HTMLElement>(
        "[data-radix-scroll-area-viewport]",
      );
      if (!viewport) return;
      viewport.scrollTo({
        top: position === "end" ? viewport.scrollHeight : 0,
        behavior,
      });
    },
    [],
  );

  // Persiste o agent_type da conversa ativa para o painel de histórico agrupar
  // por agente mesmo após reload.
  useEffect(() => {
    if (!activeAgent || !activeConversationId) return;
    setConversationAgentType(activeConversationId, activeAgent);
    setConversations((prev) =>
      prev.map((c) =>
        c.id === activeConversationId ? { ...c, agent_type: activeAgent } : c,
      ),
    );
  }, [activeAgent, activeConversationId, setConversations]);

  // O greeting da Lia nunca é renderizado: no welcome o AgentHubPicker cumpre o
  // papel de boas-vindas; em conversas persistidas ele era redundante.
  const visibleMessages = messages.filter((m) => m.id !== GREETING.id);

  const messagesCountBeforeEmailPanel = extractionResult
    ? extractionConfirmed
      ? Math.max(0, visibleMessages.length - 2)
      : visibleMessages.length
    : undefined;

  // Scroll para o início quando o chat já vier aberto (primeira vez na sessão)
  useEffect(() => {
    if (isActive && startRef.current) {
      if (!hasChatState()) {
        clearScrolledToStart();
      }

      if (!hasScrolledToStart()) {
        markScrolledToStart();
        setTimeout(() => {
          scrollChatTo(startRef, "start", "smooth");
        }, 300);
      }
    }
  }, [isActive, scrollChatTo]);

  useEffect(() => {
    if (isActive && endRef.current) {
      setTimeout(() => {
        scrollChatTo(endRef, "end", "instant");
      }, 100);
    }
  }, [isActive, scrollChatTo]);

  useEffect(() => {
    if (shouldAutoScrollRef.current && endRef.current) {
      scrollChatTo(endRef, "end", "smooth");
    }
  }, [messages, extractionResult, scrollChatTo]);

  const { sendMessage } = useSendMessageStream({
    inputValue,
    setInputValue,
    isLoading,
    setIsLoading,
    setMessages,
    appUserId,
    companyId,
    // Conversa aberta do histórico → novas mensagens são anexadas nela (não cria outra).
    conversationId: activeConversationId,
  });

  const sendMessageRef = useRef(sendMessage);
  sendMessageRef.current = sendMessage;

  // ST-5: identidade do solicitante (usuário logado) enviada no block_action ao abrir chamado.
  const requester = useMemo(
    () => ({
      nome: `${userInfo?.user?.first_name ?? ""} ${userInfo?.user?.last_name ?? ""}`.trim(),
      email: userInfo?.profile?.email,
      telefone: userInfo?.profile?.phone,
    }),
    [
      userInfo?.user?.first_name,
      userInfo?.user?.last_name,
      userInfo?.profile?.email,
      userInfo?.profile?.phone,
    ],
  );

  const formHandlers = useSendBlockAction({
    appUserId,
    companyId,
    requester,
    setMessages,
    setIsLoading,
  });

  const handleEmailConfirm = useCallback(
    (validEmails: string[], action: "remove" | "invite") => {
      const fullMessage =
        action === "remove"
          ? `Remover colaboradores: ${validEmails.join(", ")}`
          : `Adicionar colaboradores: ${validEmails.join(", ")}`;
      const displayText =
        action === "remove"
          ? "Remover colaboradores"
          : "Adicionar colaboradores";

      sendMessageRef.current(fullMessage, displayText);
      setExtractionConfirmed(true);
    },
    [],
  );

  useEffect(() => {
    registerChatMessagesUpdater(setMessages);
    return () => registerChatMessagesUpdater(null);
  }, [registerChatMessagesUpdater, setMessages]);

  // Chat aberto com mensagem pendente (ex.: insight de excedente): envia automaticamente.
  useEffect(() => {
    if (!isActive || !pendingMessage || !hydrated) return;
    sendMessage(
      pendingMessage.messageToSend,
      pendingMessage.displayText,
      pendingMessage.attachment,
    );
    clearPendingMessage();
  }, [isActive, pendingMessage, hydrated, sendMessage, clearPendingMessage]);

  // Welcome state: o usuário ainda não interagiu (nenhuma mensagem dele).
  const isWelcome = !isLoading && messages.every((m) => !m.isUser);

  const handleHubShortcutClick = (
    agentType: AgentType,
    shortcut: AgentShortcut,
  ) => {
    const displayText =
      (translate(shortcut.textKey) as string) || shortcut.action;

    setForcedAgent(agentType);
    onActionClick("CHATBOT_SEND_MESSAGE", "CHATBOT", {
      source: "agent_hub_picker",
      agent_type: agentType,
      suggestion_action: shortcut.action,
      suggestion_category: shortcut.category,
    });

    // Porta de entrada do agendamento pela IA. Fica aqui (e não nas pills) porque as duas
    // fontes de pill — AgentHubPicker e ActionCenterWelcome — convergem neste handler.
    if (shortcut.action === REPORT_SCHEDULING_ACTION) {
      onActionClick("AGENT_SUGGESTION_CLICK", "CHATBOT", {
        suggestion_id: "report_scheduling",
        suggestion_label: displayText,
        surface: "ai_central",
        source: "lia",
      });
    }

    // Action IDs com handler client-side são interceptados (mesmo fluxo das
    // suggestions): ex. action_download_template, action_upload_portfolio.
    const clientHandler = getClientSuggestionHandler(shortcut.action);
    if (clientHandler) {
      clientHandler();
      return;
    }

    sendMessage(shortcut.action, displayText, undefined, {
      forced_agent_type: agentType,
    });
  };

  const handleSuggestionClick = (suggestion: Suggestion) => {
    const clientHandler = getClientSuggestionHandler(suggestion.action);
    if (clientHandler) {
      clientHandler();
      return;
    }
    const messageToSend = suggestion.action || suggestion.text;
    // Quando o backend emite a suggestion com `text` igual ao nome interno da
    // action (ex.: "motivos_de_uso_insights"), resolve o label legível a partir
    // dos templates de greeting antes de exibir na bolha do usuário (PED-13243).
    const resolvedLabel = suggestion.action
      ? resolveDisplayTextForAction(
          suggestion.action,
          (key) => translate(key) as string | undefined,
        )
      : undefined;
    const displayText = resolvedLabel || suggestion.text;
    // Mesmo padrão do hub picker: se a action pertence a um agente (ex.:
    // "motivos_de_uso_insights" → estrategy), força o agent type no envio.
    // Sem isso o backend não sabe qual agente atende a suggestion (PED-9109).
    const forcedAgentForSuggestion = getAgentTypeForAction(suggestion.action);
    sendMessage(
      messageToSend,
      displayText,
      undefined,
      forcedAgentForSuggestion
        ? { forced_agent_type: forcedAgentForSuggestion }
        : undefined,
    );
  };

  const handleEnter = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const userInitials = `${userInfo?.user?.first_name?.[0] ?? "U"}${
    userInfo?.user?.last_name?.[0] ?? ""
  }`;

  const resetEmailExtractionUi = () => {
    setExtractionResult(null);
    setExtractionConfirmed(false);
  };

  const handleNewConversation = async (
    source: "header" | "history_panel" = "header",
  ) => {
    await actions.createNewConversation();
    onActionClick("LIA_NEW_CONVERSATION", "CHATBOT", { source });
    setIsHistoryOpen(false);
    resetEmailExtractionUi();
    setInputValue("");
    setForcedAgent(null);
    setActiveAgent(null);
  };

  const handleOpenHistory = async () => {
    onActionClick("LIA_HISTORY_OPEN", "CHATBOT");
    await actions.refreshConversations();
    setIsHistoryOpen(true);
  };

  const handleCloseHistory = () => {
    setIsHistoryOpen(false);
  };

  const handleSelectConversation = async (conversationId: string) => {
    await actions.selectConversation(conversationId);
    onActionClick("LIA_HISTORY_REOPEN", "CHATBOT", {
      conversation_id: conversationId,
    });
    setIsHistoryOpen(false);
    resetEmailExtractionUi();
    setInputValue("");
  };

  const handleRemoveConversation = async (conversationId: string) => {
    await actions.removeConversation(conversationId);
    onActionClick("LIA_HISTORY_DELETE", "CHATBOT", {
      conversation_id: conversationId,
    });
    resetEmailExtractionUi();
    setInputValue("");
  };

  return {
    // estado
    inputValue,
    setInputValue,
    isLoading,
    hydrated,
    isWelcome,
    userInitials,
    userInfo,
    // mensagens e histórico
    messages,
    visibleMessages,
    conversations,
    activeConversationId,
    // upload de planilha
    extractionResult,
    setExtractionResult,
    extractionConfirmed,
    setExtractionConfirmed,
    messagesCountBeforeEmailPanel,
    resetEmailExtractionUi,
    // refs de scroll
    endRef,
    startRef,
    shouldAutoScrollRef,
    // painel de histórico (janela flutuante)
    isHistoryOpen,
    setIsHistoryOpen,
    // ações
    sendMessage,
    formHandlers,
    handleEmailConfirm,
    handleHubShortcutClick,
    handleSuggestionClick,
    handleEnter,
    handleNewConversation,
    handleOpenHistory,
    handleCloseHistory,
    handleSelectConversation,
    handleRemoveConversation,
    refreshConversations: actions.refreshConversations,
  };
}
